/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS 'AS IS' AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { graphql, compose } from 'react-apollo'
import gql from 'graphql-tag'
import { withRouter } from 'react-router-dom'
import { isEqual } from 'lodash'
import * as Sentry from '@sentry/browser'

import {
  Spinner,
  SvgIcon,
  Table,
  PageNavigationButtons,
  CenteredContainerDisplay,
  SpanWithData,
  Warning
} from '../'

import {
  PageNavigationControl,
  readablePhoneNumber,
  saveCursor,
  decodeBase64,
  getPageCursor,
  upperCaseFirstCharacter
} from '../../utils'

class Identities extends React.Component {
  static propTypes = {
    identities: PropTypes.object,
    search: PropTypes.string,
    employmentType: PropTypes.string,
    employmentStatus: PropTypes.string,
    membershipStatus: PropTypes.string,
    productsStatus: PropTypes.string,
    createdAtStartDate: PropTypes.string,
    createdAtEndDate: PropTypes.string,
    identityType: PropTypes.string,
    employer: PropTypes.string,
    division: PropTypes.string,
    employerGroup: PropTypes.string,
    showActionColumn: PropTypes.bool,
    recordsPerPage: PropTypes.number,
    orderBy: PropTypes.string,
    changeSort: PropTypes.func.isRequired
  }

  static defaultProps = {
    recordsPerPage: 15,
    selectableRows: false
  }

  state = {
    filterChanged: false,
    pageNav: new PageNavigationControl(),
    selectedRows: []
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    // if props/filters have changed and user has not navigated back from detail view
    if ((this.props.search !== nextProps.search ||
      this.props.employmentType !== nextProps.employmentType ||
      this.props.employmentStatus !== nextProps.employmentStatus ||
      this.props.membershipStatus !== nextProps.membershipStatus ||
      this.props.productsStatus !== nextProps.productsStatus ||
      this.props.createdAtStartDate !== nextProps.createdAtStartDate ||
      this.props.createdAtEndDate !== nextProps.createdAtEndDate ||
      this.props.identityType !== nextProps.identityType ||
      this.props.employer !== nextProps.employer ||
      this.props.division !== nextProps.division ||
      this.props.employerGroup !== nextProps.employerGroup ||
      this.props.orderBy !== nextProps.orderBy
    ) &&
      !sessionStorage.getItem('identityListPageState')
    ) {
      let previousFilters = JSON.parse(sessionStorage.getItem('identityListFilters'))
      if (previousFilters && (
        previousFilters.search === nextProps.search ||
        previousFilters.employmentType === nextProps.employmentType ||
        previousFilters.employmentStatus === nextProps.employmentStatus ||
        previousFilters.membershipStatus === nextProps.membershipStatus ||
        previousFilters.productsStatus === nextProps.productsStatus ||
        previousFilters.createdAtStartDate === nextProps.createdAtStartDate ||
        previousFilters.createdAtEndDate === nextProps.createdAtEndDate ||
        previousFilters.identityType === nextProps.identityType ||
        previousFilters.employer === nextProps.employer ||
        previousFilters.division === nextProps.division ||
        previousFilters.employerGroup === nextProps.employerGroup ||
        previousFilters.orderBy === nextProps.orderBy
      )) {
        // if previous props/filters are the same as the new props, remove filters from sessionStorage
        sessionStorage.removeItem('identityListFilters')
      } else {
        // otherwise, if any filter/prop has changed, reset pagination button state and refetch data, removing filters from sessionStorage
        this.state.pageNav.reset()
        this.setState({ filterChanged: true }, () => this.props.refetch())
        sessionStorage.removeItem('identityListFilters')
      }
    } else {
      // identity listing page state was stored in sessionStorage, an indicator that user navigated back from
      // identity detail page OR user is paginating through results
      if (!nextProps.loading) {
        if (this.state.filterChanged) {
          this.setState({ filterChanged: false })
          this.state.pageNav.setPageStates(null, nextProps.identities)
        }
        else if (nextProps.identities) {
          let identityListPageState = JSON.parse(sessionStorage.getItem('identityListPageState'))
          let sameResultsAsBefore = isEqual(this.props.identities, nextProps.identities)
          if (identityListPageState) {
            this.state.pageNav.setPageStates(null, null, identityListPageState)
            sessionStorage.removeItem('identityListPageState')
            this.props.refetch({ cursor: identityListPageState.cursor })
          } else if (!sameResultsAsBefore) {
            this.state.pageNav.setPageStates(this.props.identities, nextProps.identities)
          } else if (sameResultsAsBefore) {
            this.state.pageNav.reset()
          } else {
            Sentry.captureMessage('Identity listing pagination button state logic exception')
          }
        }
      }
    }
  }

  getDetailViewUrl = () => {
    switch(this.props.identityType) {
      case 'client':
        return '/os/identity'

      case 'employer':
        return '/os/identity'

      case 'contact':
        return '/os/identity'

      default:
        return this.props.match.path
    }
  }

  savePaginationAndFilterState = (startCursor) => {
    sessionStorage.setItem('identityListPageState', JSON.stringify({
      firstPage: this.state.pageNav.firstPage,
      lastPage: this.state.pageNav.lastPage,
      currentPage: this.state.pageNav.currentPage,
      pages: this.state.pageNav.pages,
      cursor: saveCursor(this.props.identities.pageInfo.startCursor)
    }))
    sessionStorage.setItem('identityListFilters', JSON.stringify({
      search: this.props.search,
      employmentType: this.props.employmentType,
      employmentStatus: this.props.employmentStatus,
      membershipStatus: this.props.membershipStatus,
      productsStatus: this.props.productsStatus,
      createdAtStartDate: this.props.createdAtStartDate,
      createdAtEndDate: this.props.createdAtEndDate,
      identityType: this.props.identityType,
      employer: this.props.employer,
      division: this.props.division,
      employerGroup: this.props.employerGroup,
      orderBy: this.props.orderBy
    }))
  }

  saveStateAndGoToDetailPage = (identityId, startCursor) => {
    this.savePaginationAndFilterState(startCursor)
    this.props.history.push(`${this.getDetailViewUrl()}/${identityId}`)
  }

  render() {
    if (this.props.loading) {
      return (
        <CenteredContainerDisplay
          content={
            <Spinner className='slds-is-relative' />
          }
          yAdjustPercentage={-15}
        />
      )
    }

    if (this.props.error) {
      return (
        <Warning className='slds-m-around_medium'>
          Error loading identity data.
        </Warning>
      )
    }

    if (this.props.identities.edges.length === 0) {
      return (
        <div className='slds-m-around_large'>
          <SvgIcon
            variant='utility'
            symbol='info'
            colour='light'
            size='small'
            className='slds-m-right_small'
          />
          No identities found.
        </div>
      )
    }

    let pageNav = this.state.pageNav
    let tableColumns = this.props.identityType === 'employer'
      ? ['Name', 'Cellphone', 'Employer group', 'Membership type / status', 'Products status']
      : this.props.identityType === 'contact'
        ? ['Name', 'ID number', 'Position', 'Contact detail', 'Employer', 'Membership type / status']
        : ['Name', 'ID number', 'Cellphone', 'Employer / division', 'Employment type / status', 'Membership type / status', 'Products status', 'EarnBacks status']

    let startCursor = this.props.identities.pageInfo.startCursor
    let totalCount = this.props.identities.totalCount

    if (this.props.actionColumnRender) {
      tableColumns.push('Action')
    }
    return (
      <div className={`slds-scrollable_x ${this.props.className ? this.props.className : ''}`}>
        <Table
          columns={tableColumns}
          rows={this.props.identities.edges.map(identity => (
            <IdentityDetailsRow
              identity={identity.node}
              identityType={this.props.identityType}
              onClick={identityId => this.saveStateAndGoToDetailPage(identityId, this.props.identities.pageInfo.startCursor)}
              actionColumnRender={this.props.actionColumnRender}
              key={`identityDetailsRow_${identity.node.id}`}
            />
          ))}
          objectRows={true}
          orderBy={this.props.orderBy}
          changeSort={this.props.changeSort}
          querySortableColumns={{
            'Name': {
              orderByName: 'first_name'
            }
          }}
          selectableRows={this.props.selectableRows}
          selectedRows={this.state.selectedRows}
          onGlobalSelect={() => {
            if (this.state.selectedRows.length === this.props.identities.edges.length ) {
              this.setState({ selectedRows: [] })  // deselect all
            } else {
              this.setState({ selectedRows: this.props.identities.edges.map(rr => rr.node.id) })  // select all row ids
            }
          }}
          onRowSelect={e => {
            const id = e.target.id.split('-')[1]  // 'checbox-' removed from id (see Checkbox)
            this.setState(prevState => {
              if (prevState.selectedRows.includes(id)) {
                const selectedRows = [...prevState.selectedRows]
                selectedRows.splice(selectedRows.indexOf(id), 1)  // selected rows minus deselected
                return { selectedRows }
              } else {
                return { selectedRows: [...prevState.selectedRows, id] }
              }
            })
          }}
        />
        <PageNavigationButtons
          onNextClick={() => pageNav.nextPage(this.props.fetchNext)}
          onPreviousClick={() => pageNav.previousPage(this.props.fetchPrevious)}
          previousDisabled={pageNav.firstPage}
          nextDisabled={pageNav.lastPage}
          startCursor={startCursor}
          totalCount={totalCount}
          resultsPerPage={this.props.recordsPerPage}
          resultsText='identities'
          onPageNumberClick={pageNumber => this.props.refetch({ cursor: getPageCursor(pageNumber, this.props.recordsPerPage) })}
          className="slds-p-around_medium slds-float_right"
        />
      </div>
    )
  }
}

const getEmployerDivision = (identity, identityType, onClick) => {
  switch(identityType) {
    case 'client':
      return `${identity.client.division.employer.friendlyName} / ${identity.client.division.name}`

    case 'employer':
      return 'n/a'

    case 'contact':
      return (
        identity.contacts.edges.map(contact => (
          contact.node.employer
            ? <div key={`employerContact_${contact.node.id}`}>
                <a
                  className={onClick ? 'clickable' : null}
                  onClick={() => onClick
                    ? onClick(identity.id)
                    : false
                  }>
                  {contact.node.employer.friendlyName}
                </a>
              </div>
            : 'Unknown'
        ))
      )

    default:
      break
  }
}

const getIdentityDetailId = (identity) => {
  if (identity.isClient) {
    return identity.id
  }

  if (identity.isEmployer) {
    return identity.id
  }
}

const getContactPosition = (identity) => (
  identity.contacts.edges.map(contact => (
    contact.node.position
  ))
  .filter(position => position)
  .join(',\n')
)

const getContactAddress = (identity) => {
  let defaultAddress = identity.addresses.edges.find(address => (
    identity.defaultAddressType === address.node.addressType
  ))

  return defaultAddress
    ? getReadableAddressValue(defaultAddress.addressValue, defaultAddress.addressType)
    : getAlternateActiveAddress(identity.addresses)
}

const getReadableAddressValue = (addressValue, addressType) => {
  if (['MSISDN', 'LANDLINE', 'FAX'].indexOf(addressType) > -1) {
    return readablePhoneNumber(addressValue)
  } else {
    return addressValue
  }
}

const getAlternateActiveAddress = (addresses) => {
  let activeAddress = addresses.edges.find(address => (
    !address.node.inactive
    && !address.node.optedOut
    && address.node.default
  ))

  return activeAddress
    ? activeAddress.node.addressValue
    : ''
}

const IdentityDetailsRow = ({ identity, identityType, onClick }) => (
  identityType === 'employer'
    ? <tr
        id={identity.id}
        className={onClick ? 'clickable' : null}
        onClick={() => onClick
          ? onClick(getIdentityDetailId(identity))
          : false
        }>
        <td data-label='Name'>
          {identity.isEmployer
            ? identity.employer.friendlyName
            :`${identity.firstName} ${identity.lastName}${identity.otherName ? ` (${identity.otherName})` : ''}`
          }
        </td>
        <td data-label='Cellphone'>
          {identity.cellphoneNumber.edges.length > 0
            ? readablePhoneNumber(identity.cellphoneNumber.edges[0].node.addressValue)
            : '-'
          }
        </td>
        <td data-label='Employer group'>
          {identity.employer.employergroup
            ? identity.employer.employergroup.friendlyName
            : '-'
          }
        </td>
        <td data-label='Membership type / status'>
          {identity.membershipType
            ? upperCaseFirstCharacter(identity.membershipType)
            : 'Unknown'
          }
          {` / `}
          {identity.membershipStatus
            ? <SpanWithData forField='IdentityMembershipStatus' searchKey={identity.membershipStatus} />
            : 'Unknown'
          }
        </td>
        <td data-label='Products status'>
          {identity.productsStatus
            ? <SpanWithData forField='IdentityProductsStatus' searchKey={identity.productsStatus} />
            : 'Unknown'
          }
        </td>
      </tr>
    : identityType === 'contact'
      ? <tr id={identity.id}>
          <td data-label='Name'>
            {`${identity.firstName} ${identity.lastName}${identity.otherName ? ` (${identity.otherName})` : ''}`}
          </td>
          <td data-label='ID number'>
            {identity.isEmployer
              ? 'n/a'
              : identity.identityValue
            }
          </td>
          <td data-label='Position / Role' style={{ whiteSpace: 'pre' }}>
            {getContactPosition(identity)}
          </td>
          <td data-label='Contact detail'>
            {getContactAddress(identity)}
          </td>
          <td data-label='Employer' style={{ whiteSpace: 'pre' }}>
            {getEmployerDivision(identity, identityType, onClick)}
          </td>
          <td data-label='Membership type / status'>
            {identity.membershipType
              ? upperCaseFirstCharacter(identity.membershipType)
              : 'Unknown'
            }
            {` / `}
            {identity.membershipStatus
              ? <SpanWithData forField='IdentityMembershipStatus' searchKey={identity.membershipStatus} />
              : 'Unknown'
            }
          </td>
        </tr>
      : <tr
          id={identity.id}
          className={onClick ? 'clickable' : null}
          onClick={() => onClick
            ? onClick(getIdentityDetailId(identity))
            : false
          }>
          <td data-label='Name'>
            {identity.isEmployer
              ? identity.employer.friendlyName
              :`${identity.firstName} ${identity.lastName}${identity.otherName ? ` (${identity.otherName})` : ''}`
            }
          </td>
          <td data-label='ID number'>
            {identity.isEmployer
              ? 'n/a'
              : identity.identityValue
            }
          </td>
          <td data-label='Cellphone'>
            {identity.cellphoneNumber.edges.length > 0
              ? readablePhoneNumber(identity.cellphoneNumber.edges[0].node.addressValue)
              : '-'
            }
          </td>
          <td data-label='Employer / division'>
            {getEmployerDivision(identity, identityType)}
          </td>
          <td data-label='Employment type / status'>
            {identity.isClient
              ? identity.client.employmentType
                ? <React.Fragment>
                    <SpanWithData forField='ClientEmploymentType' searchKey={identity.client.employmentType} />
                    {` / `}
                    <SpanWithData forField='ClientStatus' searchKey={identity.client.status} />
                  </React.Fragment>
                : 'Unknown'
              : 'n/a'
            }
          </td>
          <td data-label='Membership type / status'>
            {identity.membershipType
              ? upperCaseFirstCharacter(identity.membershipType)
              : 'Unknown'
            }
            {` / `}
            {identity.membershipStatus
              ? <SpanWithData forField='IdentityMembershipStatus' searchKey={identity.membershipStatus} />
              : 'Unknown'
            }
          </td>
          <td data-label='Products status'>
            {identity.productsStatus
              ? <SpanWithData forField='IdentityProductsStatus' searchKey={identity.productsStatus} />
              : 'Unknown'
            }
          </td>
          <td data-label='EarnBacks status'>
            {identity.earnbacksStatus
              ? <SpanWithData forField='IdentityEarnbacksStatus' searchKey={identity.earnbacksStatus} />
              : 'Unknown'
            }
          </td>
        </tr>
)

const getIdentities = gql`
  query($employmentType: String, $employmentStatus: String, $earnbacksStatus: String, $membershipStatus: String, $productsStatus: String, $search: String, $employer: UUID, $division: UUID, $contactEmployer: UUID, $contactDivision: UUID, $employerGroup: UUID, $createdAtStartDate: DateTime, $createdAtEndDate: DateTime, $clientIsNull: Boolean, $employerIsNull: Boolean!, $contactIsNull: Boolean, $orderBy: String!, $cursor: String, $recordsPerPage: Int!) {
    identities(client_EmploymentType: $employmentType, client_Status: $employmentStatus, earnbacksStatus: $earnbacksStatus, membershipStatus: $membershipStatus, productsStatus: $productsStatus, search: $search, client_Division_Employer_Id: $employer, client_Division_Id: $division, employer_Employergroup_Id: $employerGroup, contacts_Employer_Id: $contactEmployer, contacts_Division_Id: $contactDivision, createdAt_Gte: $createdAtStartDate, createdAt_Lte: $createdAtEndDate, client_Id_Isnull: $clientIsNull, employer_Id_Isnull: $employerIsNull, contacts_Id_Isnull: $contactIsNull, orderBy: $orderBy, first: $recordsPerPage, after: $cursor) {
      totalCount
      edges {
        node {
          id
          firstName
          lastName
          otherName
          identityValue
          membershipType
          earnbacksStatus
          membershipStatus
          productsStatus
          isClient
          isEmployer
          isContact
          defaultAddressType
          client {
            id
            employmentType
            status
            division {
              id
              name
              employer {
                id
                friendlyName
              }
            }
          }
          employer {
            id
            friendlyName
            employergroup {
              id
              friendlyName
            }
          }
          addresses {
            edges {
              node {
                id
                addressType
                addressValue
                inactive
                default
                optedOut
              }
            }
          }
          cellphoneNumber: addresses(addressType: "msisdn", default: true) {
            edges {
              node {
                id
                addressValue
              }
            }
          }
          contacts {
            edges {
              node {
                id
                position
                role
                employer {
                  id
                  friendlyName
                }
              }
            }
          }
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`

const getPreviousIdentities = gql`
  query($employmentType: String, $employmentStatus: String, $earnbacksStatus: String, $membershipStatus: String, $productsStatus: String, $search: String, $employer: UUID, $division: UUID, $contactEmployer: UUID, $contactDivision: UUID, $employerGroup: UUID, $createdAtStartDate: DateTime, $createdAtEndDate: DateTime, $clientIsNull: Boolean, $employerIsNull: Boolean!, $contactIsNull: Boolean, $orderBy: String!, $cursor: String, $recordsPerPage: Int!) {
    identities(client_EmploymentType: $employmentType, client_Status: $employmentStatus, earnbacksStatus: $earnbacksStatus, membershipStatus: $membershipStatus, productsStatus: $productsStatus, search: $search, client_Division_Employer_Id: $employer, client_Division_Id: $division, employer_Employergroup_Id: $employerGroup, contacts_Employer_Id: $contactEmployer, contacts_Division_Id: $contactDivision, createdAt_Gte: $createdAtStartDate, createdAt_Lte: $createdAtEndDate, client_Id_Isnull: $clientIsNull, employer_Id_Isnull: $employerIsNull, contacts_Id_Isnull: $contactIsNull, orderBy: $orderBy, last: $recordsPerPage, before: $cursor) {
      totalCount
      edges {
        node {
          id
          firstName
          lastName
          otherName
          identityValue
          membershipType
          earnbacksStatus
          membershipStatus
          productsStatus
          isClient
          isEmployer
          isContact
          defaultAddressType
          client {
            id
            employmentType
            status
            division {
              id
              name
              employer {
                id
                friendlyName
              }
            }
          }
          employer {
            id
            friendlyName
            employergroup {
              id
              friendlyName
            }
          }
          addresses {
            edges {
              node {
                id
                addressType
                addressValue
                inactive
                default
                optedOut
              }
            }
          }
          cellphoneNumber: addresses(addressType: "msisdn", default: true) {
            edges {
              node {
                id
                addressValue
              }
            }
          }
          contacts {
            edges {
              node {
                id
                position
                role
                employer {
                  id
                  friendlyName
                }
              }
            }
          }
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`

const withData =
  graphql(getIdentities, {
    options: ({ search, employmentType, employmentStatus, earnbacksStatus, membershipStatus, productsStatus, createdAtStartDate, createdAtEndDate, identityType, employer, division, employerGroup, recordsPerPage, orderBy, cursor }) => ({
      variables: {
        search,
        employmentType,
        employmentStatus,
        earnbacksStatus,
        membershipStatus,
        productsStatus,
        employer: identityType === 'contact'
          ? undefined
          : employer ? decodeBase64(employer) : undefined,
        division: identityType === 'contact'
          ? undefined
          : division ? decodeBase64(division) : undefined,
        contactEmployer: identityType === 'contact'
          ? employer ? decodeBase64(employer) : undefined
          : undefined,
        contactDivision: identityType === 'contact'
          ? division ? decodeBase64(division) : undefined
          : undefined,
        employerGroup: employerGroup ? decodeBase64(employerGroup) : undefined,
        createdAtStartDate,
        createdAtEndDate,
        clientIsNull: identityType === 'client' ? false : undefined,
        employerIsNull: identityType === 'employer' ? false : true,
        contactIsNull: identityType === 'contact' ? false : undefined,
        orderBy: orderBy ? orderBy : 'first_name',
        recordsPerPage: recordsPerPage || 15,
        cursor
      },
      fetchPolicy: 'network-only'
    }),
    props: ({ ownProps: { search, employmentType, employmentStatus, earnbacksStatus, membershipStatus, productsStatus, createdAtStartDate, createdAtEndDate, identityType, employer, division, employerGroup, recordsPerPage, orderBy }, data: { loading, error, refetch, identities, fetchMore } }) => ({
      loading,
      error,
      refetch,
      identities,
      fetchNext: () => {
        return fetchMore({
          query: getIdentities,
          variables: {
            search,
            employmentType,
            employmentStatus,
            earnbacksStatus,
            membershipStatus,
            productsStatus,
            employer: identityType === 'contact'
              ? undefined
              : employer ? decodeBase64(employer) : undefined,
            division: identityType === 'contact'
              ? undefined
              : division ? decodeBase64(division) : undefined,
            contactEmployer: identityType === 'contact'
              ? employer ? decodeBase64(employer) : undefined
              : undefined,
            contactDivision: identityType === 'contact'
              ? division ? decodeBase64(division) : undefined
              : undefined,
            employerGroup: employerGroup ? decodeBase64(employerGroup) : undefined,
            createdAtStartDate,
            createdAtEndDate,
            clientIsNull: identityType === 'client' ? false : undefined,
            employerIsNull: identityType === 'employer' ? false : true,
            contactIsNull: identityType === 'contact' ? false : undefined,
            orderBy: orderBy ? orderBy : 'first_name',
            recordsPerPage: recordsPerPage || 15,
            cursor: identities.pageInfo.endCursor
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            const newEdges = fetchMoreResult.identities.edges
            const pageInfo = fetchMoreResult.identities.pageInfo
            return {
              identities: {
                __typename: previousResult.identities.__typename,
                edges: [...newEdges],
                pageInfo
              }
            }
          }
        })
      },
      fetchPrevious: () => {
        return fetchMore({
          query: getPreviousIdentities,
          variables: {
            search,
            employmentType,
            employmentStatus,
            earnbacksStatus,
            membershipStatus,
            productsStatus,
            employer: identityType === 'contact'
              ? undefined
              : employer ? decodeBase64(employer) : undefined,
            division: identityType === 'contact'
              ? undefined
              : division ? decodeBase64(division) : undefined,
            contactEmployer: identityType === 'contact'
              ? employer ? decodeBase64(employer) : undefined
              : undefined,
            contactDivision: identityType === 'contact'
              ? division ? decodeBase64(division) : undefined
              : undefined,
            employerGroup: employerGroup ? decodeBase64(employerGroup) : undefined,
            createdAtStartDate,
            createdAtEndDate,
            clientIsNull: identityType === 'client' ? false : undefined,
            employerIsNull: identityType === 'employer' ? false : true,
            contactIsNull: identityType === 'contact' ? false : undefined,
            orderBy: orderBy ? orderBy : 'first_name',
            recordsPerPage: recordsPerPage || 15,
            cursor: identities.pageInfo.startCursor
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            const newEdges = fetchMoreResult.identities.edges
            const pageInfo = fetchMoreResult.identities.pageInfo
            return {
              identities: {
                __typename: previousResult.identities.__typename,
                edges: [...newEdges],
                pageInfo
              }
            }
          }
        })
      }
    })
  })

export default compose(
  withData,
  withRouter
)(Identities)



// WEBPACK FOOTER //
// ./src/components/identities/index.js